<template>
  <div class="max-w-6xl mx-auto px-4 py-8">
    <div class="mb-8 border-b border-blog-gold pb-4">
      <h1 class="f-font-times-new-roman text-3xl sm:text-4xl font-bold text-blog-old-red tracking-tight">
        {{ translate('buying history', 'ucfirst') }}
      </h1>
    </div>

    <Tabs v-model:value="activeTab" class="mb-8">
      <TabList class="border-b border-blog-gold">
        <Tab 
          v-for="(item, index) of tabs" 
          :key="index" 
          :value="item.value"
          class="f-font-times-new-roman font-semibold tracking-wide text-gray-600 border-b-2 border-transparent hover:text-blog-red px-4 py-3 transition-colors duration-200 cursor-pointer"
        >
          {{ translate(item.title, 'ucfirst') }}
        </Tab>
      </TabList>

      <LoadingDialog :visible="loadingPayments" />
      
      <TabPanels class="mt-6">

        <div class="hidden md:block bg-[#fff8f1] border border-blog-gold rounded-2xl shadow-md overflow-hidden p-2">
          <DataTable 
            :value="paymentsList" 
            responsiveLayout="stack" 
            breakpoint="960px"
            class="p-datatable-sm custom-vintage-table"
          >
            <Column field="title" :header="translate('item', 'ucfirst')">
              <template #body="slotProps">
                <div class="flex items-center gap-3">
                  <div class="p-2.5 bg-white border border-blog-gold rounded-xl text-blog-red shrink-0 shadow-sm">
                    <i class="pi pi-sparkles text-sm"></i>
                  </div>
                  <div>
                    <span v-if="slotProps.data.items_count > 1" class="text-xs text-blog-red font-bold uppercase tracking-wider block mb-1">
                      + {{ slotProps.data.items_count }} {{ translate('purchased items') }}
                    </span>
                    <div class="space-y-1">
                      <a 
                        v-for="(item, index) of slotProps.data.items" 
                        :key="index" 
                        @click="redirectToProduct(slotProps.data, item)" 
                        class="cursor-pointer text-blog-old-red hover:text-blog-red font-bold text-sm block leading-tight hover:underline transition-colors"
                      >
                        {{ item.title }}
                      </a>
                    </div>
                  </div>
                </div>
              </template>
            </Column>

            <Column field="date" :header="translate('date', 'ucfirst')" class="text-xs font-semibold text-gray-600 tracking-wider"></Column>

            <Column :header="translate('total price', 'ucfirst')">
              <template #body="slotProps">
                <span class="f-font-times-new-roman font-bold text-lg text-blog-old-red font-mono">
                  {{ slotProps.data.total }}
                </span>
              </template>
            </Column>

            <Column :header="translate('payment type', 'ucfirst')" class="text-xs text-gray-600">
              <template #body="slotProps">
                <span class="inline-block bg-white px-2.5 py-1 rounded-md border border-blog-gold text-xs font-medium uppercase text-gray-700">
                  {{ translate(slotProps.data.payment_method, 'ucfirst') }}
                </span>
              </template>
            </Column>

            <Column class="text-right">
                <template #body="slotProps">
                    <div class="flex flex-wrap md:flex-nowrap gap-2 justify-end items-center">
                    <Button 
                        v-if="slotProps.data.is_approved" 
                        :label="translate('receipt', 'upper')" 
                        icon="pi pi-receipt"
                        size="small"
                        @click="viewReceipt(slotProps.data.payment_id)" 
                        class="!py-1.5 !px-3.5 !text-xs !font-bold bg-button" 
                    />
                    <span v-else class="text-xs text-gray-400 italic my-auto px-2">
                        {{ translate('unavailable', 'ucfirst') }}
                    </span>

                    <Button 
                        :label="translate('invoice', 'upper')" 
                        icon="pi pi-file"
                        size="small" 
                        @click="viewInvoice(slotProps.data.payment_id)"
                        class="!py-1.5 !px-3.5 !text-xs !font-bold no-bg-button" 
                    />
                    </div>
                </template>
            </Column>

            <template #empty>
              <div class="py-12 text-center text-gray-500">
                <i class="pi pi-inbox text-3xl text-blog-gold mb-2 block"></i>
                <p class="f-font-times-new-roman text-lg italic">{{ translate('no purchases found', 'ucfirst') }}.</p>
              </div>
            </template>
          </DataTable>
        </div>

        <div class="md:hidden space-y-4">
          <div 
            v-for="payment in paymentsList" 
            :key="payment.payment_id" 
            class="bg-[#fff8f1] border border-blog-gold rounded-2xl p-5 shadow-sm space-y-4"
          >
            <div class="flex justify-between items-center pb-3 border-b border-blog-gold">
              <span class="text-xs font-bold uppercase tracking-wider text-gray-500">{{ payment.date }}</span>
              <span class="f-font-times-new-roman font-bold text-xl text-blog-old-red font-mono">
                {{ payment.total }}
              </span>
            </div>

            <div>
              <p class="text-xs font-bold text-blog-red uppercase tracking-wider mb-2" v-if="payment.items_count > 1">
                + {{ payment.items_count }} {{ translate('purchased items') }}
              </p>
              <div class="flex flex-col gap-2 bg-white p-3 rounded-xl border border-blog-gold">
                <a 
                  v-for="item in payment.items" 
                  :key="item.uuid"
                  @click="goToCoursePage(item.uuid)"
                  class="text-blog-old-red hover:text-blog-red font-bold text-sm leading-snug cursor-pointer flex items-center gap-2"
                >
                  <i class="pi pi-sparkles text-blog-red text-[10px] shrink-0"></i>
                  <span>{{ item.title }}</span>
                </a>
              </div>
            </div>

            <div class="flex items-center justify-between text-xs text-gray-600 pt-1">
              <span class="uppercase tracking-wider font-semibold text-gray-400">Método:</span>
              <span class="font-medium text-gray-700 bg-white px-2 py-0.5 rounded border border-blog-gold">
                {{ translate(payment.payment_method, 'ucfirst') }}
              </span>
            </div>

            <div class="flex gap-2 pt-3 border-t border-blog-gold">
                <Button 
                    v-if="payment.is_approved" 
                    :label="translate('receipt', 'upper')"
                    icon="pi pi-receipt"
                    size="small"
                    class="flex-1 !py-2 !px-3 !text-xs !font-bold bg-button" 
                    @click="viewReceipt(payment.payment_id)" 
                />
                
                <Button 
                    :label="translate('invoice', 'upper')"
                    icon="pi pi-file"
                    size="small"
                    class="flex-1 !py-2 !px-3 !text-xs !font-bold no-bg-button" 
                    @click="viewInvoice(payment.payment_id)" 
                />
                </div>
          </div>
          
          <div v-if="paymentsList.length === 0" class="text-center py-12 bg-[#fff8f1] border border-blog-gold rounded-2xl">
            <i class="pi pi-inbox text-3xl text-blog-gold mb-2 block"></i>
            <p class="f-font-times-new-roman text-lg italic text-gray-600">{{ translate('no purchases found', 'ucfirst') }}.</p>
          </div>
        </div>

      </TabPanels>
    </Tabs>
  </div>
</template>

<script setup>
import { ref, onMounted, watch } from 'vue';
import DataTable from 'primevue/datatable';
import Column from 'primevue/column';
import Tabs from 'primevue/tabs';
import TabList from 'primevue/tablist';
import Tab from 'primevue/tab';
import TabPanels from 'primevue/tabpanels';
import Button from 'primevue/button';
import { translate } from '@/composables/translate';
import LoadingDialog from '@/components/Utils/LoadingDialog.vue';
import paymentService from '@/services/PaymentService';
import urlHandler from '@/composables/urlHandler';
import { showToast } from '@/composables/toast';
import { useToast } from 'primevue';

const tabs = ref([
    { title: 'approved', value: '2' },
    { title: 'pending', value: '1' },
    { title: 'refused', value: '3' },
    { title: 'expired', value: '4' },
]);
const activeTab = ref('2');
const loadingPayments = ref(true);
const toast = useToast();

onMounted(() => {
    getPayments();
});

watch(activeTab, (newValue) => {
    if (newValue) {
        getPayments();
    }
});

const paymentsList = ref([]);
async function getPayments() {
    loadingPayments.value = true;

    try {
        const payments = await paymentService.getPaymentsByStatus(activeTab.value);
        paymentsList.value = payments.map(payment => payment.simplifyData());
    } catch (error) {
        paymentsList.value = [];
    } finally {
        loadingPayments.value = false;
    }
}

const viewReceipt = (payment_id = '') => {
    paymentService.openMercadoPagoReceiptPage(payment_id);
};

const viewInvoice = (payment_id) => {
    urlHandler.redirectAndScroll(`/dashboard/payment_invoice/${payment_id}`);
};

function goToCoursePage(courseUuid = null) {
    if (courseUuid) {
        urlHandler.redirectAndScroll(`/course/${courseUuid}`);
    } else {
        showToast(toast, translate('course not found', 'ucfirst'), 'info');
    }
}

function goToSellingMaterialGroupPage(sellingMaterialGroupId = null) {
    if (sellingMaterialGroupId) {
        urlHandler.redirectAndScroll(`/dashboard/teacher_materials`, {selected: sellingMaterialGroupId});
    } else {
        showToast(toast, translate('material not found', 'ucfirst'), 'info');
    }
}

function redirectToProduct(payment = null, item = null) {
    switch (payment.product_type) {
        case 'course':
            goToCoursePage(item.uuid);
            break;
        case 'sellingmaterialgroup':
            goToSellingMaterialGroupPage(item.id);
            break;
    }
}
</script>

<style scoped>
/* Reset de bordas das abas personalizadas */
:deep(.p-tablist-tab-list) {
    border-bottom: 1px solid #d4af37 /* ou var(--blog-gold) */;
    background-color: transparent;
}

/* Customização das Tabs ativas para o estilo vintage */
:deep(.p-tab-active) {
    color: #8b0000 !important; /* blog-old-red */
    border-color: #8b0000 !important;
    font-weight: 700;
}

/* Customização visual do DataTable */
:deep(.custom-vintage-table) {
    background-color: transparent;
}

:deep(.custom-vintage-table .p-datatable-thead > tr > th) {
    background-color: transparent;
    color: #8b0000;
    font-family: 'Times New Roman', Times, serif;
    font-size: 0.95rem;
    font-weight: 700;
    border-bottom: 1px solid rgba(212, 175, 55, 0.4);
    padding: 1rem 0.75rem;
}

:deep(.custom-vintage-table .p-datatable-tbody > tr) {
    background-color: transparent;
    border-bottom: 1px solid rgba(212, 175, 55, 0.2);
}

:deep(.custom-vintage-table .p-datatable-tbody > tr:last-child) {
    border-bottom: none;
}

:deep(.custom-vintage-table .p-datatable-tbody > tr > td) {
    padding: 1rem 0.75rem;
}
</style>